/**
 * Pure anonymous aggregation + spaced echo selection.
 * Used both in the Worker (for real cycles via Workflow) and in the frontend
 * for immediate visual feedback + demo mode.
 * 
 * This is the "spaced repetition" intelligence: we surface previous threads
 * (without attribution) so the group can notice shifts over "repetitions".
 */

import type { Thread, Aggregates, Echo } from './types'

export function computeAggregates(threads: Thread[]): Aggregates {
  if (threads.length === 0) {
    return {
      n: 0,
      avgFrag: 0, avgRecovery: 0, avgReadiness: 0, avgClarity: 0,
      highFragCount: 0, highRecoveryCount: 0, lowClarityCount: 0,
      topEmotionNames: [],
      patternSentences: ['The loom is empty. The first threads will define the initial tension.']
    }
  }

  const n = threads.length
  const sum = threads.reduce((acc, t) => ({
    frag: acc.frag + t.frag,
    recovery: acc.recovery + t.recovery,
    readiness: acc.readiness + t.readiness,
    clarity: acc.clarity + t.clarity,
  }), { frag: 0, recovery: 0, readiness: 0, clarity: 0 })

  const avgFrag = sum.frag / n
  const avgRecovery = sum.recovery / n
  const avgReadiness = sum.readiness / n
  const avgClarity = sum.clarity / n

  const highFragCount = threads.filter(t => t.frag >= 7).length
  const highRecoveryCount = threads.filter(t => t.recovery >= 7).length
  const lowClarityCount = threads.filter(t => t.clarity <= 4).length

  // Simple but effective "top emotions" — frequency + recency bias
  const emotionCounts = new Map<string, number>()
  threads.forEach((t, i) => {
    const weight = 1 + (i / Math.max(1, n)) * 0.6 // slight recency
    emotionCounts.set(t.emotionName, (emotionCounts.get(t.emotionName) || 0) + weight)
  })
  const topEmotionNames = Array.from(emotionCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name]) => name)

  // Pattern sentences — deterministic, non-AI, grounded in the four axes.
  // Designed to make invisible load visible while staying respectful.
  const sentences: string[] = []

  if (avgFrag > 6.6 && avgRecovery > 6.3) {
    sentences.push('High fragmentation is traveling with high recovery need. The system is scattering focus faster than the body can restore itself.')
  } else if (avgFrag > 6.2) {
    sentences.push('Attention is fragmenting across most threads. Many contributions carry the cost of constant context-switching and vigilance.')
  }

  if (highRecoveryCount / n > 0.55) {
    sentences.push('More than half the weave records elevated recovery need. Rest and reconnection are not optional extras here — they are structural.')
  }

  if (avgClarity < 4.8 && avgReadiness < 5.5) {
    sentences.push('Post-reset clarity remains low while readiness is also compromised. The group is being asked to perform while still metabolizing the last demand.')
  } else if (avgClarity > 6.8) {
    sentences.push('Despite the loads named, post-reset clarity is holding. Something in the shared naming or the spacing is helping the threads settle.')
  }

  if (highFragCount > 0 && lowClarityCount > 0 && highFragCount / n > 0.4) {
    sentences.push('A recurring cluster: high fragmentation paired with low post-reset clarity. The cost is not only in the moment — it lingers.')
  }

  if (sentences.length === 0) {
    sentences.push('The cloth is still forming. Each new thread changes the tension for everyone who follows.')
  }

  // Always add one sentence about the emotional vocabulary itself
  if (topEmotionNames.length >= 2) {
    sentences.push(`The most repeated namings right now: ${topEmotionNames.slice(0, 3).join(' • ')}.`)
  }

  return {
    n,
    avgFrag: Math.round(avgFrag * 10) / 10,
    avgRecovery: Math.round(avgRecovery * 10) / 10,
    avgReadiness: Math.round(avgReadiness * 10) / 10,
    avgClarity: Math.round(avgClarity * 10) / 10,
    highFragCount,
    highRecoveryCount,
    lowClarityCount,
    topEmotionNames,
    patternSentences: sentences.slice(0, 4)
  }
}

/**
 * Select spaced echoes.
 * Strategy: prefer threads from earlier cycles that have not been echoed much,
 * and that sit at interesting positions in the four-dimensional space (outliers or strong representatives).
 */
export function selectEchoes(threads: Thread[], previousEchoIds: string[] = [], max = 3): Echo[] {
  if (threads.length === 0) return []

  // Exclude ones already heavily echoed in recent cycles
  const candidates = threads.filter(t => !previousEchoIds.includes(t.id))

  if (candidates.length === 0) return []

  // Score: older cycle first, plus "signal strength" (distance from mean or extreme values)
  const scored = candidates.map(t => {
    const age = 100 - Math.min(t.cycle, 99) // prefer earlier cycles
    const extremeness = Math.abs(t.frag - 5) + Math.abs(t.recovery - 5) + Math.abs(t.clarity - 5)
    return { thread: t, score: age * 1.6 + extremeness * 0.8 + (t.recovery > 7 ? 3 : 0) }
  })

  scored.sort((a, b) => b.score - a.score)

  return scored.slice(0, max).map(({ thread }) => ({
    threadId: thread.id,
    emotionName: thread.emotionName,
    snippet: thread.note.length > 78 ? thread.note.slice(0, 75) + '…' : thread.note,
    fromCycle: thread.cycle
  }))
}

export function makeManifest(threads: Thread[]): string {
  if (threads.length === 0) return 'No threads yet. The first naming will set the first tension in the warp.'
  // Create a quiet, powerful found-poem from the emotion names.
  // Order is roughly chronological but we interleave a little for rhythm.
  const names = threads.map(t => t.emotionName)
  // Simple weave of the names
  let text = names.slice(0, 11).join('  ·  ')
  if (names.length > 11) text += '  ·  …'
  return text
}
